import type { GraphNode } from '@/lib/apiTypes'
import type { Direction, Depth } from '@/features/graph/lib/graphView'
import { ChipIcon, DatabaseIcon, BoxIcon } from '@/shared/components/Icons'
import s from '@/features/graph/components/GraphStatusBar.module.css'

/**
 * Says what is narrowing the view, and gets you out of it in one click.
 *
 * Pinned to the top of the canvas so it never moves. The control it replaces was a "✕ Clear"
 * button at the end of a wrapping toolbar — it only appeared when filter chips were active, it
 * shifted position as chips reflowed, and it cleared only filters, not the search or the
 * focused node. With four independent ways to narrow the view, one fixed, honest readout is
 * load-bearing.
 *
 * The count is taken from the SAME computation that produced the canvas, so "12 of 148" cannot
 * disagree with what is drawn. The old toolbar counted a pre-layout node set while the canvas
 * drew a different one.
 */

const KIND_GLYPH = {
  state: <DatabaseIcon size={11} />,
  module: <BoxIcon size={11} />,
  provider: <ChipIcon size={11} />,
} as const

const DIR_LABEL: Record<Direction, string> = {
  up: '↑ upstream',
  both: '↕ both ways',
  down: '↓ downstream',
}

export interface GraphStatusBarProps {
  shown: number
  total: number
  focused: GraphNode | null
  direction: Direction
  depth: Depth
  activeFilterCount: number
  hiddenCount: number
  searchTerm: string
  onClearFocus: () => void
  onClearFilters: () => void
  onClearSearch: () => void
  onUnhideAll: () => void
  onResetAll: () => void
  onOpenFilters: () => void
}

export function GraphStatusBar({
  shown, total, focused, direction, depth, activeFilterCount, hiddenCount, searchTerm,
  onClearFocus, onClearFilters, onClearSearch, onUnhideAll, onResetAll, onOpenFilters,
}: GraphStatusBarProps) {
  const narrowed = !!focused || activeFilterCount > 0 || hiddenCount > 0 || searchTerm.trim().length > 0
  if (!narrowed) return null

  return (
    <div className={s.bar} role="status">
      {focused && (
        <span className={s.chip}>
          <span className={s.chipGlyph}>{KIND_GLYPH[focused.kind]}</span>
          <span className={s.chipLabel}>{focused.label}</span>
          <span className={s.chipMeta}>
            {DIR_LABEL[direction]}{depth !== 'all' ? ` · ${depth} hop${depth === 1 ? '' : 's'}` : ''}
          </span>
          <button className={s.chipX} aria-label="Stop isolating this node" onClick={onClearFocus}>✕</button>
        </span>
      )}

      {activeFilterCount > 0 && (
        <span className={s.chip}>
          <button className={s.chipBtn} onClick={onOpenFilters}>
            {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'}
          </button>
          <button className={s.chipX} aria-label="Clear filters" onClick={onClearFilters}>✕</button>
        </span>
      )}

      {searchTerm.trim().length > 0 && (
        <span className={s.chip}>
          <span className={s.chipLabel}>“{searchTerm.trim()}”</span>
          <button className={s.chipX} aria-label="Clear search" onClick={onClearSearch}>✕</button>
        </span>
      )}

      {hiddenCount > 0 && (
        <span className={s.chip}>
          <button className={s.chipBtn} onClick={onUnhideAll}>
            {hiddenCount} hidden
          </button>
          <button className={s.chipX} aria-label="Unhide all" onClick={onUnhideAll}>✕</button>
        </span>
      )}

      <span className={s.count}>{shown} of {total} shown</span>

      <button className={s.reset} onClick={onResetAll} title="Clear everything and show the whole graph (Esc)">
        Reset ✕
      </button>
    </div>
  )
}
